import { Anchor, Box, Button, Center, Container, Paper, PasswordInput, Stack, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { MIN_PASSWORD } from '../components/change-password';
import { Logo } from '../components/logo';
import { TopLoadBar } from '../components/top-load-bar';

// Where the identity provider's reset email lands: the token from the link,
// a new password twice, then sign-in. Every session of the account ended
// when the password changed.
export const Route = createFileRoute('/reset-password')({
  validateSearch: (s: Record<string, unknown>): { token?: string | undefined } => (typeof s['token'] === 'string' ? { token: s['token'] } : {}),
  component: ResetPassword,
});

interface Answer { status?: string; reason?: string; message?: string }

function ResetPassword() {
  const { token } = Route.useSearch();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const form = useForm({
    initialValues: { next: '', again: '' },
    validate: {
      next: (v) => (v.length >= MIN_PASSWORD ? null : `Use at least ${MIN_PASSWORD} characters.`),
      again: (v, values) => (v === values.next ? null : 'The two passwords differ.'),
    },
  });
  const submit = async ({ next }: typeof form.values) => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch('/auth/reset-password', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token, password: next }) });
      const a = (await res.json().catch(() => ({ status: 'refused' }))) as Answer;
      if (a.status === 'ok') { setDone(true); return; }
      form.setFieldError('next', a.reason === 'reset_refused' && a.message ? a.message : a.reason === 'rate_limited' ? 'Too many attempts. Wait a few minutes before trying again.' : 'This reset link is not valid any more. Ask for a new one from the sign-in page.');
    } catch {
      form.setFieldError('next', 'The password could not be changed. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box component="main" mih="100dvh" style={{ display: 'grid', gridTemplateRows: '1fr auto' }}>
      <Center p="md">
        <Container size={420} w="100%" p={0}>
          <Stack gap="xs" align="center">
            <Logo height={112} mb={24} />
            <Title order={1} ta="center">Choose a new password</Title>
            <Text c="dimmed" size="sm" ta="center">For your MotionSafe Console account.</Text>
          </Stack>
          <Paper withBorder p={22} mt={30} radius="md" pos="relative">
            {/* Submitting says so along the card's top edge, as the tablet's sign-in card does. */}
            <TopLoadBar active={busy} />
            {done ? (
              <Stack gap="lg">
                <Text>Your password is changed, and every session that used the old one has ended.</Text>
                <Button component="a" href="/" fullWidth>Sign in</Button>
              </Stack>
            ) : !token ? (
              <Stack gap="lg">
                <Text>This page needs the link from the reset email. Open the email and use its link, or ask for a new one from the sign-in page.</Text>
                <Anchor href="/" size="sm">Go to sign-in</Anchor>
              </Stack>
            ) : (
              <form onSubmit={form.onSubmit((v) => void submit(v))}>
                <Stack gap="lg">
                  <PasswordInput autoFocus label="New password" description={`At least ${MIN_PASSWORD} characters; longer is better than complicated.`} autoComplete="new-password" size="md" {...form.getInputProps('next')} />
                  <PasswordInput label="New password again" autoComplete="new-password" size="md" {...form.getInputProps('again')} />
                  <Button type="submit" fullWidth disabled={busy}>Change password</Button>
                </Stack>
              </form>
            )}
          </Paper>
        </Container>
      </Center>
      <Box component="footer" p="md" ta="center">
        <Text size="xs" c="dimmed">Operators sign in on a device, not here.</Text>
      </Box>
    </Box>
  );
}
